"use client"

import { useMemo, useState } from "react"
import CdnImage from "@modules/common/components/cdn-image"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import {
  buildMessengerHref,
  buildWhatsAppHref,
} from "@modules/layout/components/floating-contact-launcher/helpers"
import { SizeGuideCategory } from "../lib/fetch-size-guide"
import { buildMeasurementSteps } from "../lib/measurement-guides"

type Unit = "inch" | "cm"

type Props = {
  categories: SizeGuideCategory[]
  whatsappNumber?: string | null
  facebookUrl?: string | null
}

const CUSTOM_FIT_MESSAGE =
  "Assalamu alaikum. I would like to request a custom-tailored fit. My measurements are:"

function RulerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-5 w-5">
      <path
        d="M3.5 8.5 8.5 3.5l12 12-5 5-12-12Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M7 9.5 8.5 11M9.5 7 11.5 9M11 5.5 14 8.5M16 13l1.5 1.5M13 16l2 2"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

function ScissorsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-7 w-7">
      <circle cx="6" cy="6" r="2.4" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="6" cy="18" r="2.4" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 7.6 20 17M8 16.4 20 7M8.2 9.5 13 12l-4.8 2.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
      <path
        fill="currentColor"
        d="M20.5 3.5A11.8 11.8 0 0 0 12.1 0C5.6 0 .3 5.3.3 11.8c0 2.1.5 4.1 1.6 5.9L0 24l6.5-1.7c1.7.9 3.7 1.4 5.6 1.4 6.5 0 11.8-5.3 11.8-11.8 0-3.2-1.2-6.1-3.4-8.4ZM12.1 21.7c-1.8 0-3.5-.5-5-1.3l-.4-.2-3.8 1 1-3.7-.2-.4a9.8 9.8 0 0 1-1.5-5.2c0-5.4 4.4-9.8 9.9-9.8 2.6 0 5.1 1 7 2.9a9.8 9.8 0 0 1 2.9 7c-.1 5.3-4.5 9.7-9.9 9.7Zm5.4-7.4c-.3-.1-1.8-.9-2.1-1-.3-.1-.5-.1-.7.2-.2.3-.8 1-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.5-.6c.2-.2.2-.3.3-.5.1-.2.1-.4 0-.6-.1-.1-.7-1.7-.9-2.3-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.6.1-.9.4-.3.3-1.1 1-1.1 2.5s1.1 3 1.3 3.2c.1.2 2.1 3.3 5.2 4.6.7.3 1.3.5 1.8.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.5Z"
      />
    </svg>
  )
}

function MessengerIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
      <path
        fill="currentColor"
        d="M12 0C5.2 0 0 5 0 11.7c0 3.5 1.4 6.5 3.8 8.6V24l3.5-1.9c1.4.4 3 .7 4.7.7 6.8 0 12-5 12-11.7S18.8 0 12 0Zm1.2 15.7-3.1-3.3-6.1 3.3 6.7-7.1 3.2 3.3L19.8 8l-6.6 7.7Z"
      />
    </svg>
  )
}

export default function FitIntelligence({
  categories,
  whatsappNumber,
  facebookUrl,
}: Props) {
  const [activeId, setActiveId] = useState(categories[0]?.id ?? "")
  const [unit, setUnit] = useState<Unit>("inch")
  const [customFitOpen, setCustomFitOpen] = useState(false)

  const active = useMemo(
    () => categories.find((c) => c.id === activeId) ?? categories[0],
    [categories, activeId]
  )

  const steps = useMemo(
    () => buildMeasurementSteps(active?.columns),
    [active]
  )

  const channels = useMemo(() => {
    const wa = buildWhatsAppHref(whatsappNumber)
    const fb = buildMessengerHref(facebookUrl)
    return {
      whatsapp: wa ? `${wa}?text=${encodeURIComponent(CUSTOM_FIT_MESSAGE)}` : null,
      messenger: fb,
    }
  }, [whatsappNumber, facebookUrl])

  const hasChannels = Boolean(channels.whatsapp || channels.messenger)

  if (!active) {
    return null
  }

  const displayValue = (
    value: string | number,
    type: "label" | "measurement"
  ) => {
    if (type === "label") return String(value)
    const n = parseFloat(String(value))
    if (Number.isNaN(n)) return String(value)
    if (unit === "cm") return (n * 2.54).toFixed(1)
    return String(value)
  }

  const unitSuffix = (type: "label" | "measurement") => {
    if (type !== "measurement") return ""
    return unit === "inch" ? '"' : " cm"
  }

  // Smart variation note — pulled from the chart's own description / fit guide,
  // never the bogus "size up 2-4 inches" boilerplate.
  const variationNote = [active.description, active.fit_guide]
    .map((s) => (s ? s.trim() : ""))
    .filter(Boolean)
    .join(" ")

  return (
    <section className="bg-white">
      <div className="content-container py-10 small:py-14">
        {/* Toolbar: category tabs + unit toggle */}
        <div className="flex flex-col gap-5 border-b border-alif-rule pb-4 small:flex-row small:items-center small:justify-between">
          <div
            className="no-scrollbar -mx-6 flex gap-1 overflow-x-auto px-6 small:mx-0 small:px-0"
            role="tablist"
            aria-label="Size chart categories"
          >
            {categories.map((cat) => {
              const isActive = cat.id === active.id
              return (
                <button
                  key={cat.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveId(cat.id)}
                  className={`alif-label shrink-0 whitespace-nowrap border-b-2 px-3 pb-3 pt-1 transition-colors ${
                    isActive
                      ? "border-alif-green text-alif-green"
                      : "border-transparent text-alif-muted hover:text-alif-ink"
                  }`}
                >
                  {cat.label}
                </button>
              )
            })}
          </div>

          {/* Unit toggle (default inches) */}
          <div className="flex shrink-0 items-center gap-3">
            <span
              className={`alif-label transition-opacity ${
                unit === "inch" ? "text-alif-ink" : "text-alif-muted opacity-50"
              }`}
            >
              Inches
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={unit === "cm"}
              aria-label="Toggle measurement unit between inches and centimetres"
              onClick={() => setUnit((u) => (u === "inch" ? "cm" : "inch"))}
              className="relative h-6 w-11 rounded-full bg-alif-green/90 transition-colors"
            >
              <span
                className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  unit === "cm" ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
            <span
              className={`alif-label transition-opacity ${
                unit === "cm" ? "text-alif-ink" : "text-alif-muted opacity-50"
              }`}
            >
              CM
            </span>
          </div>
        </div>

        {/* Table + custom-fit CTA */}
        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="overflow-x-auto rounded-xl border border-alif-rule">
              <table className="w-full border-collapse text-left text-sm">
                <caption className="sr-only">
                  {active.label} garment measurements in{" "}
                  {unit === "inch" ? "inches" : "centimetres"}
                </caption>
                <thead>
                  <tr className="bg-alif-ink text-white">
                    {active.columns.map((col) => (
                      <th
                        key={col.key}
                        scope="col"
                        className="alif-label whitespace-nowrap px-5 py-4 text-left"
                      >
                        {col.label}
                        {col.type === "measurement" && (
                          <span className="ml-1 font-normal normal-case tracking-normal text-white/60">
                            ({unit})
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {active.rows.map((row, i) => (
                    <tr
                      key={i}
                      className={`border-t border-alif-rule transition-colors hover:bg-alif-green-soft ${
                        i % 2 === 0 ? "bg-white" : "bg-alif-ivory-deep/60"
                      }`}
                    >
                      {active.columns.map((col) => (
                        <td
                          key={col.key}
                          className={`whitespace-nowrap px-5 py-3.5 ${
                            col.type === "label"
                              ? "font-semibold text-alif-ink"
                              : "text-alif-muted"
                          }`}
                        >
                          {displayValue(row[col.key] ?? "", col.type)}
                          {row[col.key] !== undefined &&
                          row[col.key] !== "" &&
                          row[col.key] !== "-"
                            ? unitSuffix(col.type)
                            : ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Smart variation note (replaces the bogus "size up 2-4 inches") */}
            {variationNote && (
              <div className="mt-5 flex items-start gap-3 rounded-lg bg-alif-ivory-deep px-4 py-3.5">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                  className="mt-0.5 h-4 w-4 shrink-0 text-alif-green"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                  <path
                    d="M12 11v5M12 7.8h.01"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
                <p className="text-sm leading-relaxed text-alif-muted">
                  {variationNote}
                </p>
              </div>
            )}
          </div>

          {/* Custom Tailoring CTA */}
          <div className="lg:col-span-1">
            <div className="flex h-full flex-col items-center justify-center rounded-xl border border-alif-green-line bg-alif-green-soft p-8 text-center">
              <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white text-alif-green">
                <ScissorsIcon />
              </span>
              <h2 className="alif-serif text-2xl text-alif-ink">
                Custom Tailoring Available
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-alif-muted">
                Between sizes or need a specialised fit? Our master tailors can
                craft a garment to your exact measurements.
              </p>

              {hasChannels ? (
                <div className="mt-6 w-full">
                  {!customFitOpen ? (
                    <button
                      type="button"
                      onClick={() => setCustomFitOpen(true)}
                      className="alif-label w-full rounded-full bg-alif-green px-6 py-3.5 text-white transition-colors hover:bg-alif-green-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-alif-green focus-visible:ring-offset-2"
                    >
                      Request Custom Fit
                    </button>
                  ) : (
                    <div className="space-y-2.5">
                      <p className="text-xs text-alif-muted">
                        Choose a channel to message us:
                      </p>
                      {channels.whatsapp && (
                        <a
                          href={channels.whatsapp}
                          target="_blank"
                          rel="noreferrer"
                          className="flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1ebe5d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2"
                        >
                          <WhatsAppIcon />
                          WhatsApp
                        </a>
                      )}
                      {channels.messenger && (
                        <a
                          href={channels.messenger}
                          target="_blank"
                          rel="noreferrer"
                          className="flex w-full items-center justify-center gap-2 rounded-full bg-[#0084FF] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0676dd] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0084FF] focus-visible:ring-offset-2"
                        >
                          <MessengerIcon />
                          Facebook
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <LocalizedClientLink
                  href="/contact"
                  className="alif-label mt-6 w-full rounded-full bg-alif-green px-6 py-3.5 text-white transition-colors hover:bg-alif-green-deep"
                >
                  Request Custom Fit
                </LocalizedClientLink>
              )}
            </div>
          </div>
        </div>

        {/* How to Measure — dynamic per category */}
        <div className="mt-16 grid grid-cols-1 gap-10 border-t border-alif-rule pt-12 small:gap-14 medium:grid-cols-2 medium:items-center">
          <div>
            <p className="alif-label text-alif-gold">{active.label}</p>
            <h2 className="alif-serif mt-2 text-3xl text-alif-ink small:text-4xl">
              How to Measure
            </h2>
            {active.how_to_measure && (
              <p className="mt-3 text-sm leading-relaxed text-alif-muted">
                {active.how_to_measure}
              </p>
            )}
            <div className="mt-8 space-y-7">
              {steps.map((step) => (
                <div key={step.key} className="flex gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-alif-green-soft text-alif-green">
                    <RulerIcon />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-medium text-alif-ink">
                      {step.title}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-alif-muted">
                      {step.instruction}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative aspect-[4/5] w-full overflow-hidden rounded-xl bg-alif-ivory-deep medium:aspect-auto medium:h-[600px]">
            {active.image ? (
              <CdnImage
                key={active.image}
                src={active.image}
                alt={active.image_alt}
                fill
                sizes="(max-width: 1280px) 100vw, 50vw"
                className="object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-alif-muted">
                <span className="alif-serif text-2xl">{active.label}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
